import type {
  CommandId,
  MessageId,
  ModelSelection,
  ProviderInteractionMode,
  RuntimeMode,
} from "@cmmd-forge/contracts";

interface SubmissionIdentitySnapshot {
  readonly prompt: string;
  readonly attachments: ReadonlyArray<{ readonly id: string }>;
  readonly files?: ReadonlyArray<{ readonly id: string }>;
  readonly terminalContexts: ReadonlyArray<{ readonly id: string }>;
  readonly startedAt: string;
  readonly commandId?: CommandId;
  readonly messageId?: MessageId;
  readonly modelSelection?: ModelSelection;
  readonly runtimeMode?: RuntimeMode;
  readonly interactionMode?: ProviderInteractionMode;
  readonly executionIntent?: { readonly runtimeId: string };
}

interface CurrentSubmissionSnapshot {
  readonly prompt: string;
  readonly attachmentIds: ReadonlyArray<string>;
  readonly fileIds?: ReadonlyArray<string>;
  readonly terminalContextIds: ReadonlyArray<string>;
  readonly modelSelection?: ModelSelection;
  readonly runtimeMode?: RuntimeMode;
  readonly interactionMode?: ProviderInteractionMode;
}

function idsMatch(
  prior: ReadonlyArray<{ readonly id: string }>,
  current: ReadonlyArray<string>,
): boolean {
  return (
    prior.length === current.length && prior.every((item, index) => item.id === current[index])
  );
}

function modelSelectionsMatch(left: ModelSelection, right: ModelSelection): boolean {
  return (
    left.provider === right.provider &&
    left.model === right.model &&
    ("providerAccountId" in left ? left.providerAccountId : undefined) ===
      ("providerAccountId" in right ? right.providerAccountId : undefined) &&
    JSON.stringify(left.options ?? null) === JSON.stringify(right.options ?? null)
  );
}

function hasDispatchSettings(input: {
  readonly modelSelection?: ModelSelection;
  readonly runtimeMode?: RuntimeMode;
  readonly interactionMode?: ProviderInteractionMode;
}): input is {
  readonly modelSelection: ModelSelection;
  readonly runtimeMode: RuntimeMode;
  readonly interactionMode: ProviderInteractionMode;
} {
  return (
    input.modelSelection !== undefined &&
    input.runtimeMode !== undefined &&
    input.interactionMode !== undefined
  );
}

function dispatchSettingsMatch(
  prior: SubmissionIdentitySnapshot,
  current: CurrentSubmissionSnapshot,
): boolean {
  if (!hasDispatchSettings(prior)) return true;
  if (!hasDispatchSettings(current)) return false;
  if (!modelSelectionsMatch(prior.modelSelection, current.modelSelection)) return false;
  if (prior.runtimeMode !== current.runtimeMode) return false;
  return prior.interactionMode === current.interactionMode;
}

function composerSubmissionContentMatches(
  prior: Pick<SubmissionIdentitySnapshot, "prompt" | "attachments" | "files" | "terminalContexts">,
  current: Pick<
    CurrentSubmissionSnapshot,
    "prompt" | "attachmentIds" | "fileIds" | "terminalContextIds"
  >,
): boolean {
  return (
    prior.prompt === current.prompt &&
    idsMatch(prior.attachments, current.attachmentIds) &&
    idsMatch(prior.files ?? [], current.fileIds ?? []) &&
    idsMatch(prior.terminalContexts, current.terminalContextIds)
  );
}

export function resolveComposerSubmissionIdentity(input: {
  readonly priorSubmission: SubmissionIdentitySnapshot | null | undefined;
  readonly current: CurrentSubmissionSnapshot;
  readonly now: () => string;
  readonly createCommandId: () => CommandId;
  readonly createMessageId: () => MessageId;
}): {
  readonly replaysPriorSubmission: boolean;
  readonly commandId: CommandId;
  readonly messageId: MessageId;
  readonly startedAt: string;
  readonly replayRuntimeId?: string;
} {
  const prior = input.priorSubmission;
  const replaysPriorSubmission =
    prior?.commandId !== undefined &&
    prior.messageId !== undefined &&
    composerSubmissionContentMatches(prior, input.current) &&
    dispatchSettingsMatch(prior, input.current);
  if (replaysPriorSubmission) {
    return {
      replaysPriorSubmission: true,
      commandId: prior.commandId,
      messageId: prior.messageId,
      startedAt: prior.startedAt,
      ...(prior.executionIntent?.runtimeId
        ? { replayRuntimeId: prior.executionIntent.runtimeId }
        : {}),
    };
  }
  return {
    replaysPriorSubmission: false,
    commandId: input.createCommandId(),
    messageId: input.createMessageId(),
    startedAt: input.now(),
  };
}
